import { FC, useState } from 'react';
import {
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import { WbHeaderActionButton } from '../WbHeaderActionButton';
import HelpIcon from '@material-ui/icons/Help';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { usePermission } from '@backstage/plugin-permission-react';
import { platformSettingsEditPermission } from '@agilelab/plugin-wb-rbac-common';
import { WbIcon } from '../WbIcon/WbIcon';
import { useHelpLinksConfig, HelpLink } from '../../hooks/useHelpLinksConfig';

const useStyles = makeStyles(theme => ({
  title: {
    padding: theme.spacing(1.5, 2, 0.5, 2),
    fontWeight: 600,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    lineHeight: 'normal',
  },
  menuItem: {
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    paddingRight: theme.spacing(2),
    gap: theme.spacing(1),
  },
  externalIcon: {
    marginLeft: 'auto',
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  paper: {
    minWidth: 240,
    maxWidth: 360,
  },
}));

const HelpLinkItem: FC<{ link: HelpLink; autoFocus?: boolean }> = ({
  link,
  ...itemProps
}) => {
  const classes = useStyles();
  return (
    <MenuItem
      {...itemProps}
      component="a"
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.menuItem}
    >
      {link.icon && <WbIcon icon={link.icon} style={{ fontSize: '1.25rem' }} />}
      <span>{link.label}</span>
      <OpenInNewIcon className={classes.externalIcon} />
    </MenuItem>
  );
};

export const WbSupportButton: FC = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { data: config } = useHelpLinksConfig();
  const { allowed: isAdmin } = usePermission({
    permission: platformSettingsEditPermission,
  });

  const items = config?.items ?? [];
  const adminItems = config?.adminItems ?? [];
  const hasStandardLinks = items.length > 0;
  const hasAdminLinks = isAdmin && adminItems.length > 0;

  if (!hasStandardLinks && !hasAdminLinks) return null;

  return (
    <>
      <WbHeaderActionButton
        color="primary"
        aria-label="Open help menu"
        aria-controls="help-menu"
        aria-haspopup="true"
        icon={<HelpIcon />}
        onClick={e => setAnchorEl(e.currentTarget)}
      />
      <Menu
        id="help-menu"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        getContentAnchorEl={null}
        PaperProps={{ className: classes.paper }}
        MenuListProps={{
          'aria-labelledby': 'help-menu-label',
          disablePadding: true,
          subheader: (
            <ListSubheader
              id="help-menu-label"
              disableSticky
              className={classes.title}
              role="presentation"
            >
              Help Menu
            </ListSubheader>
          ),
        }}
      >
        {hasStandardLinks &&
          items.map((link, i) => (
            <HelpLinkItem key={`item-${i}`} link={link} />
          ))}
        {hasAdminLinks && hasStandardLinks && (
          <Divider component="li" key="separator" />
        )}
        {hasAdminLinks &&
          adminItems.map((link, i) => (
            <HelpLinkItem key={`admin-${i}`} link={link} />
          ))}
      </Menu>
    </>
  );
};
